import React, { useMemo } from 'react';
import { useTheme } from '../../../context/ThemeContext';

export default function AitPropertyStats({ buildings }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const stats = useMemo(() => {
    if (!buildings || buildings.length === 0) return null;

    let totalUnits = 0, qualifiedCount = 0, managerCount = 0;
    let occupiedCount = 0, vacantCount = 0, leasedCount = 0, noticeCount = 0, marketRate = 0;
    const desigDist = {};
    const bedroomAgg = {};

    buildings.forEach(b => {
      const calc = b.calculation;
      if (!calc) return;

      totalUnits += calc.total_units || 0;
      qualifiedCount += calc.qualified_count || 0;
      managerCount += calc.manager_unit_count || 0;
      occupiedCount += calc.occupied_count || 0;
      vacantCount += calc.vacant_count || 0;
      leasedCount += calc.leased_count || 0;
      noticeCount += calc.notice_count || 0;
      marketRate += calc.market_rate_count || 0;

      if (calc.designation_distribution) {
        Object.entries(calc.designation_distribution).forEach(([d, c]) => {
          desigDist[d] = (desigDist[d] || 0) + c;
        });
      }

      if (calc.bedroom_stats) {
        Object.entries(calc.bedroom_stats).forEach(([br, data]) => {
          if (!bedroomAgg[br]) bedroomAgg[br] = { total: 0, qualified: 0, occupied: 0, vacant: 0, designations: {} };
          bedroomAgg[br].total += data.total || 0;
          bedroomAgg[br].qualified += data.qualified || 0;
          bedroomAgg[br].occupied += data.occupied || 0;
          bedroomAgg[br].vacant += data.vacant || 0;
          if (data.designations) {
            Object.entries(data.designations).forEach(([d, c]) => {
              bedroomAgg[br].designations[d] = (bedroomAgg[br].designations[d] || 0) + c;
            });
          }
        });
      }
    });

    const occupancyPct = totalUnits > 0 ? ((occupiedCount / totalUnits) * 100).toFixed(1) : '0';

    return { totalUnits, qualifiedCount, managerCount, occupiedCount, vacantCount, leasedCount, noticeCount, marketRate, occupancyPct, desigDist, bedroomAgg };
  }, [buildings]);

  if (!stats || stats.totalUnits === 0) return null;

  const statCards = [
    { label: 'Total Units', value: stats.totalUnits },
    { label: 'Occupied', value: stats.occupiedCount, color: 'text-green-500' },
    { label: 'Leased', value: stats.leasedCount, color: 'text-blue-500' },
    { label: 'Notice', value: stats.noticeCount, color: stats.noticeCount > 0 ? 'text-orange-500' : '' },
    { label: 'Vacant', value: stats.vacantCount, color: stats.vacantCount > 0 ? 'text-orange-500' : '' },
    { label: 'Occupancy', value: `${stats.occupancyPct}%` },
    { label: 'Qualified', value: stats.qualifiedCount, color: 'text-blue-500' },
    { label: 'Market Rate', value: stats.marketRate },
  ];
  if (stats.managerCount > 0) statCards.push({ label: 'Manager', value: stats.managerCount });

  return (
    <div className="mb-6 space-y-3">
      {/* Counts row */}
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
        {statCards.map(s => (
          <div key={s.label} className={`px-3 py-2 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
            <p className={`text-lg font-semibold ${s.color || ''}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Set-aside by bedroom size */}
      {Object.keys(stats.bedroomAgg).length > 0 && (
        <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-xs">
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Unit Size</th>
                <th className={`text-center px-2 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</th>
                <th className={`text-center px-2 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Occ</th>
                <th className={`text-center px-2 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Vac</th>
                <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Set-Aside Breakdown</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {Object.entries(stats.bedroomAgg)
                .sort(([a], [b]) => (Number(a) || 0) - (Number(b) || 0))
                .map(([br, data]) => (
                <tr key={br} className={isDark ? 'bg-gray-800/50' : 'bg-white'}>
                  <td className="px-3 py-2 font-medium">{br != null && br !== 'null' ? `${br} BR` : 'Unknown'}</td>
                  <td className="text-center px-2 py-2">{data.total}</td>
                  <td className="text-center px-2 py-2 text-green-500">{data.occupied}</td>
                  <td className={`text-center px-2 py-2 ${data.vacant > 0 ? 'text-orange-500' : ''}`}>{data.vacant}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {Object.entries(data.designations)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([d, c]) => (
                        <span key={d} className={`px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                          {d}%:{c}
                        </span>
                      ))}
                      {Object.keys(data.designations).length === 0 && (
                        <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
